import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { useRole } from '../hooks/useRole';
import { useData } from '../context/DataContext';
import { sortProductsByCategory, getFinishBadge } from '../utils/inventorySorting';
import Sidebar from '../components/Sidebar';

const INCLUDED_CATEGORIES = [
  'Chain Link', 'Galv Pipe', 'Black Pipe', 'Galv Fittings', 'Black Fittings',
  'Aluminum', 'PVC', 'Wood', 'Wood Hardware', 'Split Rail', 'Tools/Misc',
];

function Inventory({ user }) {
  const navigate = useNavigate();
  const { isAdmin, isSalesperson, loading: roleLoading } = useRole();
  const { products, updateProduct } = useData();

  const [initializing, setInitializing] = useState(false);
  const initDoneRef = useRef(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [stockFilter, setStockFilter] = useState('all'); // 'all' | 'low' | 'out'
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());
  // The product currently selected for manual reordering (click-to-select). One
  // at a time across the whole page; the section-header ▲▼ act on it.
  const [selectedProductId, setSelectedProductId] = useState(null);

  // The product row currently being edited (any inline field focused). On the
  // Low/Out tabs its row stays visible even if the in-progress value no longer
  // matches the filter; the filter re-applies when editing ends (blur/Enter).
  const [editingId, setEditingId] = useState(null);

  // productId → { stockQty?, reorderPoint? } — pending local edits not yet saved
  const [localChanges, setLocalChanges] = useState({});
  // productId → true while saving
  const [saving, setSaving] = useState({});
  // productId → true for 2s after save
  const [savedRecently, setSavedRecently] = useState({});
  // category → string input for bulk reorder point
  const [bulkReorderInputs, setBulkReorderInputs] = useState({});
  const [bulkApplying, setBulkApplying] = useState({});

  // Debounced auto-save. Per-product timers live in a COMPONENT-LEVEL ref (not row
  // state), so a scheduled save still fires after the row unmounts — which happens
  // the instant a stock edit removes the row from the Low/Out filtered list.
  const saveTimersRef = useRef({});   // productId -> setTimeout handle
  // Per-product in-flight guard so a debounce and a blur can't double-write.
  const savingRef = useRef({});       // productId -> true while a save is in flight
  // productId -> true when a save was asked for WHILE one was in flight. The
  // in-flight save runs again when it lands, so that request is never dropped.
  const resaveRef = useRef({});
  const SAVE_DEBOUNCE_MS = 900;

  // ── THE PENDING EDITS: A REF THAT IS ALWAYS CURRENT ──
  //
  // saveProduct reads pending edits from localChangesRef, never from the
  // `localChanges` state it closed over, and every write goes through
  // setPending(), which updates the ref and the state TOGETHER, synchronously.
  //
  // WHY THIS WAS A BUG. The ref used to be assigned INSIDE a setState updater
  // function. React does not run an updater when setState is called — often it
  // runs it later, during the next render. The round-up checkbox records its tick
  // and saves in the SAME event, so its save could read the ref before the tick
  // was in it, find nothing to save, and return; and because saveProduct cancels
  // the pending debounce first, nothing was left to save it afterwards. The box
  // showed ticked from local state, and was unticked again after a reload.
  const localChangesRef = useRef(localChanges);
  const setPending = (next) => {
    localChangesRef.current = next;
    setLocalChanges(next);
  };

  // Redirect non-admins
  useEffect(() => {
    if (!roleLoading && !isAdmin) {
      alert('Inventory management is admin-only.');
      navigate('/dashboard');
    }
  }, [roleLoading, isAdmin, navigate]);

  // Initialize inventory fields for products that haven't been set up yet
  useEffect(() => {
    if (!products.length || initDoneRef.current || !isAdmin || roleLoading) return;
    initDoneRef.current = true;

    const needsInit = products.filter(p =>
      INCLUDED_CATEGORIES.includes((p.category || '').trim()) &&
      p.isActive !== false &&
      p.trackInventory === undefined
    );
    if (needsInit.length === 0) return;

    const run = async () => {
      setInitializing(true);
      try {
        for (let i = 0; i < needsInit.length; i += 400) {
          const batch = writeBatch(db);
          needsInit.slice(i, i + 400).forEach(p => {
            batch.update(doc(db, 'products', p.id), {
              trackInventory: true,
              stockQty: 0,
              reorderPoint: 10,
            });
            updateProduct(p.id, { trackInventory: true, stockQty: 0, reorderPoint: 10 });
          });
          await batch.commit();
        }
      } catch (err) {
        console.error('Inventory init error:', err);
      }
      setInitializing(false);
    };
    run();
  }, [products, isAdmin, roleLoading, updateProduct]);

  // ── Helpers ──

  const getDisplayQty = (product) =>
    localChanges[product.id]?.stockQty !== undefined
      ? localChanges[product.id].stockQty
      : (product.stockQty ?? 0);

  const getDisplayReorder = (product) =>
    localChanges[product.id]?.reorderPoint !== undefined
      ? localChanges[product.id].reorderPoint
      : (product.reorderPoint ?? 10);

  // Packaging. Mirrors the reorder getter: prefer a pending local edit, else the
  // stored value, else a default (type=Bag, qty=blank, round=off).
  //
  // THE PACK SIZE IS NOT PURELY INFORMATIONAL — it feeds the "2 full bags plus
  // 60 loose" hints beside the description, AND it is the size the Round Up
  // button rounds to on quotes and invoices. But ONLY for products whose
  // roundUpInQuotes box is ticked: the two fields answer two different
  // questions, and filling in a stock hint must never quietly start changing
  // what a customer is quoted.
  const getDisplayPackType = (product) =>
    localChanges[product.id]?.packType !== undefined
      ? localChanges[product.id].packType
      : (product.packType ?? 'Bag');

  const getDisplayPackQty = (product) =>
    localChanges[product.id]?.packQty !== undefined
      ? localChanges[product.id].packQty
      : (product.packQty ?? '');

  // Absent reads as OFF. Rounding a customer's quantity is something the owner
  // opts into per product, never something a missing field turns on.
  const getDisplayRoundUp = (product) =>
    localChanges[product.id]?.roundUpInQuotes !== undefined
      ? localChanges[product.id].roundUpInQuotes
      : (product.roundUpInQuotes === true);

  const getStatus = (product) => {
    const qty = parseFloat(getDisplayQty(product)) || 0;
    const reorder = parseFloat(getDisplayReorder(product)) || 0;
    if (qty === 0) return 'out';
    if (qty <= reorder) return 'low';
    return 'ok';
  };

  // ── Save a product ──
  const saveProduct = async (productId) => {
    // A blur and a pending debounce may both target this product — this call
    // takes over from any pending timer.
    if (saveTimersRef.current[productId]) {
      clearTimeout(saveTimersRef.current[productId]);
      delete saveTimersRef.current[productId];
    }
    // A save for this product is already in flight. This request must NOT be
    // dropped: it can carry an edit the in-flight save never saw — type a pack
    // size, then tick the round-up box while that size is still saving. It used
    // to return here, having just cancelled the only timer that would have saved
    // the tick. Now it is remembered, and the in-flight save goes round again.
    if (savingRef.current[productId]) {
      resaveRef.current[productId] = true;
      return;
    }

    // Read from the ref, NOT the closured `localChanges`, so a debounced timer sees
    // the latest queued edit rather than a stale pre-setState snapshot.
    const changes = localChangesRef.current[productId];
    if (!changes || Object.keys(changes).length === 0) return;

    const fields = {};
    if (changes.stockQty !== undefined) fields.stockQty = Math.max(0, parseFloat(changes.stockQty) || 0);
    if (changes.reorderPoint !== undefined) fields.reorderPoint = Math.max(0, parseFloat(changes.reorderPoint) || 0);
    // Packaging — never touches stock, reorder or pricing. packQty DOES feed the
    // stock hints and, for products marked below, quote rounding.
    if (changes.packType !== undefined) fields.packType = changes.packType;
    if (changes.packQty !== undefined) {
      const raw = String(changes.packQty).trim();
      // Blank stays "unset" (null) so it displays empty rather than 0.
      fields.packQty = raw === '' ? null : Math.max(0, parseFloat(raw) || 0);
    }
    // Stored as a real boolean, never a string: packageRounding.js tests it with
    // === true, so "false" from a stray form value would read as ON.
    if (changes.roundUpInQuotes !== undefined) {
      fields.roundUpInQuotes = changes.roundUpInQuotes === true;
    }

    savingRef.current[productId] = true;
    setSaving(prev => ({ ...prev, [productId]: true }));
    let saved = false;
    try {
      console.log('[Inventory] saveProduct →', { productId, fields });
      await updateDoc(doc(db, 'products', productId), fields);
      // The same fields, into the DataContext products cache (memory and
      // localStorage), so every screen in this browser sees them without a reload.
      updateProduct(productId, fields);
      // Clear ONLY the fields just saved, and only where they were not edited
      // again during the await — a value typed mid-save stays pending and is
      // saved by the follow-up below. After clearing, the row reads the newly
      // cached product, so the checkbox and the status badge stay correct.
      const cur = localChangesRef.current[productId];
      if (cur) {
        const remaining = { ...cur };
        Object.keys(changes).forEach(k => { if (remaining[k] === changes[k]) delete remaining[k]; });
        const next = { ...localChangesRef.current };
        if (Object.keys(remaining).length === 0) delete next[productId];
        else next[productId] = remaining;
        setPending(next);
      }
      setSavedRecently(prev => ({ ...prev, [productId]: true }));
      setTimeout(() => setSavedRecently(prev => { const n = { ...prev }; delete n[productId]; return n; }), 2000);
      saved = true;
    } catch (err) {
      console.error('[Inventory] saveProduct failed:', productId, fields, err);
      alert('Error saving. Please try again.');
    }
    savingRef.current[productId] = false;
    setSaving(prev => { const n = { ...prev }; delete n[productId]; return n; });

    // Anything asked for, or edited, while that save was in flight is saved now
    // rather than waiting on a timer that may already have been cancelled. Not
    // after a failure: that would retry in a loop behind the error message.
    const pendingNow = localChangesRef.current[productId];
    const again = resaveRef.current[productId] || (pendingNow && Object.keys(pendingNow).length > 0);
    delete resaveRef.current[productId];
    if (saved && again) saveProduct(productId);
  };

  // Schedule a debounced save ~SAVE_DEBOUNCE_MS after the last keystroke for this
  // product. Because the timer lives in saveTimersRef (component scope), it still
  // fires even if the row has since been filtered out and unmounted.
  const scheduleSave = (productId) => {
    if (saveTimersRef.current[productId]) clearTimeout(saveTimersRef.current[productId]);
    saveTimersRef.current[productId] = setTimeout(() => {
      delete saveTimersRef.current[productId];
      saveProduct(productId);
    }, SAVE_DEBOUNCE_MS);
  };

  // Blur safety net: persist immediately. saveProduct itself cancels the pending
  // debounce and guards against a double-write.
  const flushSave = (productId) => { saveProduct(productId); };

  // Record a pending field edit and (re)arm the debounced save. Used by every
  // inline-editable field so none of them lose edits on the Low/Out tabs.
  const updateField = (productId, field, value) => {
    // Synchronous — see setPending. A save called straight after this, in the
    // same event, sees this edit.
    const prev = localChangesRef.current;
    setPending({ ...prev, [productId]: { ...prev[productId], [field]: value } });
    scheduleSave(productId);
  };

  // ── Quick ± adjust (immediate save) ──
  // ── Bulk reorder point for a category ──
  const applyBulkReorder = async (category) => {
    const raw = bulkReorderInputs[category];
    const value = parseFloat(raw);
    if (isNaN(value) || value < 0) return;

    setBulkApplying(prev => ({ ...prev, [category]: true }));
    const catProducts = inventoryProducts.filter(p => (p.category || '').trim() === category);
    try {
      const batch = writeBatch(db);
      catProducts.forEach(p => {
        batch.update(doc(db, 'products', p.id), { reorderPoint: value });
        updateProduct(p.id, { reorderPoint: value });
      });
      await batch.commit();
      setBulkReorderInputs(prev => { const n = { ...prev }; delete n[category]; return n; });
    } catch (err) {
      console.error('Bulk reorder error:', err);
    }
    setBulkApplying(prev => { const n = { ...prev }; delete n[category]; return n; });
  };

  // ── Manual ordering (▲▼) ──
  // Lazily assign a numeric `sortOrder` to a whole category the first time it's
  // reordered — based on the CURRENT sorted display index (0,1,2,…) — so we only
  // ever touch one category's docs, never all products. If every item already has
  // a numeric sortOrder we leave them as-is. Returns an id→sortOrder map of the
  // authoritative values after ensuring (used by the swap so it's correct whether
  // the category was un-, partially-, or fully-initialized).
  const ensureCategorySortOrder = async (catItems) => {
    const allHave = catItems.every(p => typeof p.sortOrder === 'number');
    const map = {};
    if (allHave) {
      catItems.forEach(p => { map[p.id] = p.sortOrder; });
      return map;
    }
    const batch = writeBatch(db);
    catItems.forEach((p, i) => {
      batch.update(doc(db, 'products', p.id), { sortOrder: i });
      updateProduct(p.id, { sortOrder: i });
      map[p.id] = i;
    });
    await batch.commit();
    return map;
  };

  // Swap a product with its neighbor WITHIN the same category (catItems is one
  // category's sorted array, so a product can never leave its category). Persists
  // both docs via writeBatch and patches the DataContext cache via updateProduct
  // so Inventory + the picker re-sort instantly (no refetch needed).
  const handleMoveUp = async (product, catItems, index) => {
    if (index <= 0) return;
    const orderMap = await ensureCategorySortOrder(catItems);
    const neighbor = catItems[index - 1];
    const aOrder = orderMap[product.id];
    const bOrder = orderMap[neighbor.id];
    const batch = writeBatch(db);
    batch.update(doc(db, 'products', product.id), { sortOrder: bOrder });
    batch.update(doc(db, 'products', neighbor.id), { sortOrder: aOrder });
    await batch.commit();
    updateProduct(product.id, { sortOrder: bOrder });
    updateProduct(neighbor.id, { sortOrder: aOrder });
  };

  const handleMoveDown = async (product, catItems, index) => {
    if (index >= catItems.length - 1) return;
    const orderMap = await ensureCategorySortOrder(catItems);
    const neighbor = catItems[index + 1];
    const aOrder = orderMap[product.id];
    const bOrder = orderMap[neighbor.id];
    const batch = writeBatch(db);
    batch.update(doc(db, 'products', product.id), { sortOrder: bOrder });
    batch.update(doc(db, 'products', neighbor.id), { sortOrder: aOrder });
    await batch.commit();
    updateProduct(product.id, { sortOrder: bOrder });
    updateProduct(neighbor.id, { sortOrder: aOrder });
  };

  // ── Filter products ──
  const inventoryProducts = products.filter(p =>
    INCLUDED_CATEGORIES.includes((p.category || '').trim()) && p.isActive !== false
  );

  const filteredProducts = inventoryProducts.filter(p => {
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      if (!p.sku?.toLowerCase().includes(t) && !p.description?.toLowerCase().includes(t)) return false;
    }
    if (filterCategory !== 'All' && (p.category || '').trim() !== filterCategory) return false;
    // Keep the row being actively edited visible even if its in-progress value no
    // longer matches Low/Out — it re-evaluates once editing ends (editingId cleared
    // on blur/Enter). The search/category filters above still apply.
    if (p.id !== editingId) {
      const st = getStatus(p);
      if (stockFilter === 'out' && st !== 'out') return false;
      if (stockFilter === 'low' && st !== 'low') return false;
    }
    return true;
  });

  // Reordering (▲▼) needs the FULL category list. A category filter is fine — it
  // hides OTHER categories, so the shown category still contains all its items. But
  // search and stock (Low/Out) filters narrow a category to a partial subset, which
  // would corrupt per-category sortOrder, so arrows stay hidden under those.
  const canReorder = isAdmin && !searchTerm && stockFilter === 'all';

  // Reordering only applies to the full list — drop any selection while filtering.
  useEffect(() => { if (!canReorder) setSelectedProductId(null); }, [canReorder]);

  // ── Summary counts ──
  const totalTracked = inventoryProducts.length;
  const outCount = inventoryProducts.filter(p => getStatus(p) === 'out').length;
  const lowCount = inventoryProducts.filter(p => getStatus(p) === 'low').length;

  // ── Group by category (INCLUDED_CATEGORIES order) and sort within each ──
  const byCategory = INCLUDED_CATEGORIES.reduce((acc, cat) => {
    const items = filteredProducts.filter(p => (p.category || '').trim() === cat);
    if (items.length > 0) acc[cat] = sortProductsByCategory(items, cat);
    return acc;
  }, {});

  // ── Printable count sheet grouping ──
  // Deliberately built from inventoryProducts (ALL active inventory-tracked
  // products), NOT filteredProducts — the count sheet ignores the on-screen
  // search/category/stock filters and always prints everything. Same category
  // order + per-category sort as the on-screen grouping above.
  const countSheetByCategory = INCLUDED_CATEGORIES.reduce((acc, cat) => {
    const items = inventoryProducts.filter(p => (p.category || '').trim() === cat);
    if (items.length > 0) acc[cat] = sortProductsByCategory(items, cat);
    return acc;
  }, {});
  const countSheetDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const toggleCategory = (cat) =>
    setCollapsedCategories(prev => {
      const n = new Set(prev);
      n.has(cat) ? n.delete(cat) : n.add(cat);
      return n;
    });

  if (roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#CDE7F5', fontFamily: 'Manrope, sans-serif' }}>
        <p style={{ color: '#6B7C93' }}>Loading…</p>
      </div>
    );
  }
  if (!isAdmin) return null;

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: '#CDE7F5', fontFamily: 'Manrope, sans-serif' }}>

      {/* ── Sidebar ── */}
      <Sidebar />

      {/* ── Main ── */}
      <div className="ml-60 flex-1 flex flex-col min-h-screen">

        {/* Header */}
        <header className="px-8 py-4 sticky top-0 z-10" style={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E3ECF5' }}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold" style={{ color: '#03045E' }}>📦 Inventory Management</h1>
              <p className="text-sm" style={{ color: '#6B7C93' }}>Track and manage stock levels</p>
            </div>
            <div className="flex items-center gap-3">
              {initializing && (
                <span className="text-sm px-3 py-1 rounded-lg" style={{ color: '#0077B6', backgroundColor: '#E6F1FB' }}>Initializing inventory…</span>
              )}
              <button
                onClick={() => window.print()}
                className="text-sm font-semibold px-4 py-2 rounded-lg transition"
                style={{ backgroundColor: '#0077B6', color: '#FFFFFF' }}
                title="Print a blind physical-count sheet for ALL inventory products"
              >
                🖨 Print Count Sheet
              </button>
            </div>
          </div>

          {/* Summary bar */}
          <div className="flex items-center gap-6 mt-3">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold" style={{ color: '#0077B6' }}>{totalTracked}</span>
              <span className="text-sm" style={{ color: '#6B7C93' }}>products tracked</span>
            </div>
            <div className="h-5 w-px" style={{ backgroundColor: '#E3ECF5' }} />
            <button
              onClick={() => setStockFilter(f => f === 'low' ? 'all' : 'low')}
              className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm font-semibold transition"
              style={stockFilter === 'low' ? { backgroundColor: '#FDF1DD', color: '#854F0B' } : { color: '#854F0B' }}
            >
              <span>🟡</span> {lowCount} Low Stock
            </button>
            <button
              onClick={() => setStockFilter(f => f === 'out' ? 'all' : 'out')}
              className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm font-semibold transition"
              style={stockFilter === 'out' ? { backgroundColor: '#FBECEC', color: '#A32D2D' } : { color: '#A32D2D' }}
            >
              <span>🔴</span> {outCount} Out of Stock
            </button>
          </div>
        </header>

        <main className="flex-1 px-8 py-6 space-y-4">

          {/* Controls */}
          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search SKU or description…"
              className="rounded-lg px-4 py-2 text-sm w-64 focus:outline-none"
              style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
              onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'}
            />
            <select
              value={filterCategory}
              onChange={e => { setFilterCategory(e.target.value); setStockFilter('all'); }}
              className="rounded-lg px-3 py-2 text-sm focus:outline-none"
              style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
              onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'}
            >
              <option value="All">All Categories</option>
              {INCLUDED_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid #E3ECF5' }}>
              {[
                { id: 'all', label: 'All' },
                { id: 'low', label: '🟡 Low' },
                { id: 'out', label: '🔴 Out' },
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setStockFilter(f.id)}
                  className="px-3 py-2 text-sm font-semibold transition"
                  style={stockFilter === f.id ? { backgroundColor: '#0077B6', color: '#FFFFFF' } : { backgroundColor: '#FFFFFF', color: '#6B7C93' }}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {(searchTerm || filterCategory !== 'All' || stockFilter !== 'all') && (
              <button
                onClick={() => { setSearchTerm(''); setFilterCategory('All'); setStockFilter('all'); }}
                className="text-xs px-2 py-1"
                style={{ color: '#6B7C93' }}
              >
                ✕ Clear filters
              </button>
            )}
            <span className="ml-auto text-sm" style={{ color: '#6B7C93' }}>{filteredProducts.length} products</span>
          </div>

          {/* Category sections */}
          {Object.keys(byCategory).length === 0 ? (
            <div className="rounded-xl p-12 text-center" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5' }}>
              <p className="text-lg" style={{ color: '#6B7C93' }}>No products match your filters</p>
            </div>
          ) : (
            Object.entries(byCategory).map(([cat, items]) => {
              const catLow = items.filter(p => getStatus(p) === 'low').length;
              const catOut = items.filter(p => getStatus(p) === 'out').length;
              const isCollapsed = collapsedCategories.has(cat);

              // Section-arrow state: index of the selected product WITHIN this
              // category (-1 if the selection belongs to another category / none).
              const selectedIndex = selectedProductId ? items.findIndex(p => p.id === selectedProductId) : -1;
              const selInThisCat = selectedIndex !== -1;
              const canMoveUp = canReorder && selInThisCat && selectedIndex > 0;
              const canMoveDown = canReorder && selInThisCat && selectedIndex < items.length - 1;

              return (
                <div key={cat} className="rounded-xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5' }}>
                  {/* Category header — sticky so it pins under the page top bar
                      while scrolling this category's rows (opaque bg so rows don't
                      bleed through; z below the top bar's z-10). */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleCategory(cat)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCategory(cat); } }}
                    className={`w-full flex items-center justify-between px-5 py-3.5 transition text-left rounded-t-xl cursor-pointer ${isCollapsed ? 'rounded-b-xl' : ''}`}
                    style={{ position: 'sticky', top: 140, zIndex: 5, backgroundColor: '#CDE7F5', borderBottom: '1px solid #E3ECF5' }}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm" style={{ color: '#6B7C93' }}>{isCollapsed ? '▶' : '▼'}</span>
                      <span className="font-bold" style={{ color: '#03045E' }}>{cat}</span>
                      <span className="text-sm" style={{ color: '#6B7C93' }}>({items.length})</span>
                      {canReorder && (
                        <span className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => handleMoveUp(items[selectedIndex], items, selectedIndex)}
                            disabled={!canMoveUp}
                            title={selInThisCat ? 'Move selected product up' : 'Select a product in this category first'}
                            style={{ background: 'none', border: 'none', padding: '0 2px', lineHeight: 1, fontSize: 13, cursor: canMoveUp ? 'pointer' : 'default', color: canMoveUp ? '#0077B6' : '#C3CBD6' }}
                          >▲</button>
                          <button
                            onClick={() => handleMoveDown(items[selectedIndex], items, selectedIndex)}
                            disabled={!canMoveDown}
                            title={selInThisCat ? 'Move selected product down' : 'Select a product in this category first'}
                            style={{ background: 'none', border: 'none', padding: '0 2px', lineHeight: 1, fontSize: 13, cursor: canMoveDown ? 'pointer' : 'default', color: canMoveDown ? '#0077B6' : '#C3CBD6' }}
                          >▼</button>
                        </span>
                      )}
                      {catOut > 0 && (
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FBECEC', color: '#A32D2D' }}>🔴 {catOut} out</span>
                      )}
                      {catLow > 0 && (
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FDF1DD', color: '#854F0B' }}>🟡 {catLow} low</span>
                      )}
                    </div>
                    {/* Bulk reorder point */}
                    {!isCollapsed && (
                      <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                        <span className="text-xs" style={{ color: '#6B7C93' }}>Set all reorder points:</span>
                        <input
                          type="number"
                          min="0"
                          value={bulkReorderInputs[cat] ?? ''}
                          onChange={e => setBulkReorderInputs(prev => ({ ...prev, [cat]: e.target.value }))}
                          placeholder="10"
                          className="rounded px-2 py-1 text-xs w-16 focus:outline-none"
                          style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                          onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'}
                        />
                        <button
                          onClick={() => applyBulkReorder(cat)}
                          disabled={bulkApplying[cat] || !bulkReorderInputs[cat]}
                          className="text-xs disabled:opacity-50 font-semibold px-2 py-1 rounded transition"
                          style={{ backgroundColor: '#0077B6', color: '#FFFFFF' }}
                        >
                          {bulkApplying[cat] ? '…' : 'Apply'}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Product rows */}
                  {!isCollapsed && (
                    <div>
                      {/* Column headers */}
                      <div className="grid grid-cols-[2fr_4fr_6rem_6rem_13rem_8rem_5rem] gap-3 px-5 py-2 text-xs font-semibold uppercase tracking-wide" style={{ borderBottom: '1px solid #E3ECF5', color: '#6B7C93' }}>
                        <span>SKU</span>
                        <span>Description</span>
                        <span className="text-center">Stock Qty</span>
                        <span className="text-center">Reorder At</span>
                        <span className="text-center">Packaging</span>
                        <span className="text-center">Status</span>
                        <span></span>
                      </div>

                      {items.map((product, index) => {
                        const status = getStatus(product);
                        const rowBg = status === 'out' ? '#FBECEC' : status === 'low' ? '#FDF1DD' : '#FFFFFF';
                        const isSavingThis = saving[product.id];
                        const savedThis = savedRecently[product.id];
                        const displayQty = getDisplayQty(product);
                        const displayReorder = getDisplayReorder(product);
                        const displayPackType = getDisplayPackType(product);
                        const displayPackQty = getDisplayPackQty(product);
                        const displayRoundUp = getDisplayRoundUp(product);

                        return (
                          <div
                            key={product.id}
                            onClick={canReorder ? (e) => {
                              // Only the row's non-interactive area selects — never
                              // when clicking an input/button/select (inline editing).
                              if (e.target.closest('input, button, select, textarea')) return;
                              setSelectedProductId(product.id);
                            } : undefined}
                            // Mark this row as actively-editing while any of its fields
                            // holds focus (onFocus/onBlur bubble from the inputs). Clear
                            // only when focus leaves the row entirely — so tabbing between
                            // this row's fields keeps it visible on the Low/Out tabs.
                            onFocus={() => setEditingId(product.id)}
                            onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setEditingId(null); }}
                            className="grid grid-cols-[2fr_4fr_6rem_6rem_13rem_8rem_5rem] gap-3 items-center px-5 py-2.5 hover:brightness-95 transition-all"
                            style={{
                              backgroundColor: rowBg,
                              borderBottom: index === items.length - 1 ? 'none' : '1px solid #E3ECF5',
                              ...(canReorder ? { cursor: 'pointer' } : {}),
                              ...(selectedProductId === product.id ? { background: '#E6F1FB', boxShadow: 'inset 3px 0 0 #0077B6' } : {}),
                            }}
                          >
                            {/* SKU */}
                            <span className="font-mono font-bold text-xs" style={{ color: '#0077B6' }}>{product.sku || '—'}</span>

                            {/* Description + finish badge (+ ft rolls hint pushed right) */}
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-sm truncate" style={{ color: '#03045E' }}>{product.description}</span>
                              {(() => {
                                const badge = getFinishBadge(product);
                                if (!badge) return null;
                                return (
                                  <span style={{
                                    background: badge.bg, color: badge.color,
                                    border: `1px solid ${badge.border}`,
                                    padding: '2px 8px', borderRadius: 6,
                                    fontSize: 10, fontWeight: 800, letterSpacing: '0.5px',
                                    flexShrink: 0,
                                  }}>
                                    {badge.label}
                                  </span>
                                );
                              })()}
                              {/* Rolls hint — read-only, only for packType 'ft'. Sits in the spare
                                  space on the right of the description cell (no extra row height).
                                  Translates displayed stock (feet) into full rolls + partial. */}
                              {displayPackType === 'ft' && (() => {
                                const feet = parseFloat(displayQty) || 0;
                                const perRoll = parseFloat(displayPackQty) || 0;
                                let text;
                                if (perRoll <= 0) {
                                  text = 'set ft/roll';
                                } else if (feet === 0) {
                                  text = '0 rolls';
                                } else {
                                  const fullRolls = Math.floor(feet / perRoll);
                                  const remainder = Math.round((feet % perRoll) * 10) / 10; // tame float noise
                                  const rollWord = fullRolls === 1 ? 'roll' : 'rolls';
                                  if (remainder === 0) {
                                    text = `${fullRolls} full ${rollWord}`;
                                  } else if (fullRolls === 0) {
                                    text = `${remainder}' (partial roll)`;
                                  } else {
                                    text = `${fullRolls} full ${rollWord} + ${remainder}' partial`;
                                  }
                                }
                                return (
                                  <span
                                    className="text-xs whitespace-nowrap flex-shrink-0"
                                    style={{ color: '#6B7C93', marginLeft: 'auto', paddingRight: 8 }}
                                    title="Full rolls from current stock — a read-only hint"
                                  >
                                    {text}
                                  </span>
                                );
                              })()}
                              {/* Bags hint — read-only, only for packType 'Bag'. Same spot/style as
                                  the rolls hint. Translates displayed stock (pieces) into full bags
                                  + loose. Only one of ft/Bag renders per row (packType is one value). */}
                              {displayPackType === 'Bag' && (() => {
                                const pieces = parseFloat(displayQty) || 0;
                                const perBag = parseFloat(displayPackQty) || 0;
                                let text;
                                if (perBag <= 0) {
                                  text = 'set pcs/bag';
                                } else if (pieces === 0) {
                                  text = '0 bags';
                                } else {
                                  const fullBags = Math.floor(pieces / perBag);
                                  const loose = Math.round((pieces % perBag) * 10) / 10; // tame float noise
                                  const bagWord = fullBags === 1 ? 'bag' : 'bags';
                                  if (loose === 0) {
                                    text = `${fullBags} full ${bagWord}`;
                                  } else if (fullBags === 0) {
                                    text = `${loose} loose`;
                                  } else {
                                    text = `${fullBags} full ${bagWord} + ${loose} loose`;
                                  }
                                }
                                return (
                                  <span
                                    className="text-xs whitespace-nowrap flex-shrink-0"
                                    style={{ color: '#6B7C93', marginLeft: 'auto', paddingRight: 8 }}
                                    title="Full bags from current stock — a read-only hint"
                                  >
                                    {text}
                                  </span>
                                );
                              })()}
                            </div>

                            {/* Stock Qty — type directly; saves on blur */}
                            <div className="flex items-center justify-center">
                              <input
                                type="number"
                                min="0"
                                value={displayQty}
                                onChange={e => updateField(product.id, 'stockQty', e.target.value)}
                                onBlur={() => flushSave(product.id)}
                                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                className="rounded px-2 py-1 text-sm text-center w-16 focus:outline-none"
                                style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                                onFocus={e => e.target.style.borderColor = '#0077B6'} onBlurCapture={e => e.target.style.borderColor = '#E3ECF5'}
                              />
                            </div>

                            {/* Reorder Point */}
                            <input
                              type="number"
                              min="0"
                              value={displayReorder}
                              onChange={e => updateField(product.id, 'reorderPoint', e.target.value)}
                              onBlur={() => flushSave(product.id)}
                              onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                              className="rounded px-2 py-1 text-sm text-center w-20 justify-self-center focus:outline-none"
                              style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                              onFocus={e => e.target.style.borderColor = '#0077B6'} onBlurCapture={e => e.target.style.borderColor = '#E3ECF5'}
                              title="Alert when stock falls to or below this"
                            />

                            {/* PACKAGING — what a package holds, and whether to round to it.
                                This is NOT informational only, and the comment that used to
                                say so was wrong the day packQty started driving rounding.
                                What is actually true:
                                  • pack type + size feed the read-only stock hints beside
                                    the description ("2 full bags + 60 loose")
                                  • the SIZE is also what the Round Up button on quotes and
                                    invoices rounds a quantity up to
                                  • but ONLY for products with the box below ticked
                                Neither field touches stock, reorder points, pricing or the
                                estimators. */}
                            <div className="flex items-center gap-1 justify-center">
                              <select
                                value={displayPackType}
                                onChange={e => updateField(product.id, 'packType', e.target.value)}
                                onBlur={() => flushSave(product.id)}
                                className="rounded px-1 py-1 text-sm focus:outline-none"
                                style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                                onFocus={e => e.target.style.borderColor = '#0077B6'} onBlurCapture={e => e.target.style.borderColor = '#E3ECF5'}
                                title="How this product is packaged. Chooses which stock hint is shown beside the description."
                              >
                                <option value="Bag">Bag</option>
                                <option value="Bundle">Bundle</option>
                                <option value="Pallet">Pallet</option>
                                <option value="ft">ft</option>
                              </select>
                              <input
                                type="number"
                                min="0"
                                value={displayPackQty}
                                onChange={e => updateField(product.id, 'packQty', e.target.value)}
                                onBlur={() => flushSave(product.id)}
                                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                placeholder="—"
                                className="rounded px-2 py-1 text-sm text-center w-14 focus:outline-none"
                                style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                                onFocus={e => e.target.style.borderColor = '#0077B6'} onBlurCapture={e => e.target.style.borderColor = '#E3ECF5'}
                                title="How many are in one package. Feeds the stock hint, and — if the box below is ticked — the quantity the Round Up button rounds to on quotes."
                              />
                            </div>

                            {/* THE OPT-IN. Nothing rounds without this, however the
                                packaging fields are filled in. Wording says what happens
                                and where, because "round up" alone reads like a stock
                                setting and this one changes what a customer is charged
                                for. */}
                            <label
                              className="flex items-center justify-center gap-1.5 mt-1 cursor-pointer"
                              title="When ticked, the Round Up button on quotes and invoices raises this product's quantity to the next whole package. Needs a pack size above."
                            >
                              <input
                                type="checkbox"
                                checked={displayRoundUp}
                                // Record the tick (a real boolean) and save at once. Both
                                // halves used to be able to lose it — see setPending and
                                // the in-flight note in saveProduct.
                                onChange={e => { updateField(product.id, 'roundUpInQuotes', e.target.checked === true); flushSave(product.id); }}
                                className="cursor-pointer"
                                style={{ accentColor: '#0077B6' }}
                              />
                              <span className="text-xs leading-tight" style={{ color: '#6B7C93' }}>
                                Round up to full package on quotes
                              </span>
                            </label>

                            {/* Status badge */}
                            <div className="flex justify-center">
                              {status === 'ok' && (
                                <span className="text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap" style={{ backgroundColor: '#E6F7EE', color: '#0F6E56' }}>🟢 In Stock</span>
                              )}
                              {status === 'low' && (
                                <span className="text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap" style={{ backgroundColor: '#FDF1DD', color: '#854F0B' }}>🟡 Low Stock</span>
                              )}
                              {status === 'out' && (
                                <span className="text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap" style={{ backgroundColor: '#FBECEC', color: '#A32D2D' }}>🔴 Out of Stock</span>
                              )}
                            </div>

                            {/* Save indicator */}
                            <div className="w-14 text-center justify-self-center">
                              {isSavingThis && (
                                <span className="text-xs" style={{ color: '#0077B6' }}>Saving…</span>
                              )}
                              {savedThis && !isSavingThis && (
                                <span className="text-xs font-semibold" style={{ color: '#0F6E56' }}>✅ Saved</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </main>
      </div>

      {/* ── Print isolation: on print, hide everything (incl. the Sidebar, which
          this page renders — unlike ViewDocument) and reveal ONLY the count
          sheet, pinned to the top of the page. */}
      <style>{`@media print {
        body * { visibility: hidden; }
        .count-sheet-print, .count-sheet-print * { visibility: visible; }
        .count-sheet-print { position: absolute; top: 0; left: 0; width: 100%; }
      }`}</style>

      {/* ── Blind physical-count sheet ──
          Hidden on screen (display:none via `hidden`), shown only in print
          (`print:block`). Blind count: NO stock qty is rendered. */}
      <div
        className="count-sheet-print hidden print:block"
        style={{ color: '#000', background: '#fff', padding: 16, fontFamily: 'Manrope, sans-serif' }}
      >
        {/* Header */}
        <div style={{ marginBottom: 16, borderBottom: '2px solid #000', paddingBottom: 10 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>Inventory Count Sheet</h1>
          <div style={{ fontSize: 14, fontWeight: 700, marginTop: 2 }}>Atlantic Fence &amp; Supply</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 13 }}>
            <span>Date: {countSheetDate}</span>
            <span>Counted by: ______________________________</span>
          </div>
        </div>

        {/* Per-category sections (INCLUDED_CATEGORIES order, empty categories skipped) */}
        {Object.entries(countSheetByCategory).map(([cat, items]) => (
          <div key={cat} style={{ pageBreakInside: 'avoid', marginBottom: 18 }}>
            <h2 style={{ fontSize: 15, fontWeight: 800, margin: '0 0 6px', borderBottom: '1px solid #000', paddingBottom: 3 }}>
              {cat} ({items.length})
            </h2>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #000', padding: '4px 6px', width: '55%' }}>Description</th>
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #000', padding: '4px 6px', width: '20%' }}>Category</th>
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #000', padding: '4px 6px', width: '25%' }}>Count</th>
                </tr>
              </thead>
              <tbody>
                {items.map(p => (
                  <tr key={p.id} style={{ pageBreakInside: 'avoid' }}>
                    <td style={{ padding: '4px 6px', borderBottom: '1px solid #ccc' }}>{p.description}</td>
                    <td style={{ padding: '4px 6px', borderBottom: '1px solid #ccc' }}>{(p.category || '').trim()}</td>
                    <td style={{ padding: '4px 6px', borderBottom: '1px solid #ccc' }}>
                      <div style={{ border: '1px solid #000', height: 24, width: 120 }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Inventory;
